import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Play, FolderOpen, Upload } from "lucide-react";
import { logEvent } from "@/lib/analytics";
import { markDemoStarted, trackDemoPercent } from "@/lib/demoProgress";
import { DEMO_VIDEO_URL, DEMO_POSTER, DEMO_DURATION, isYouTube } from "@/lib/demo";

// Reusable, responsive, keyboard-friendly demo video modal.
// CTAs are all optional — pass only the ones relevant to the context (public vs in-app).
export function WatchDemoModal({ open, onOpenChange, onStartTrial, onBookDemo, onSample, onImport, source = "unknown" }) {
  const videoRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    markDemoStarted(source);
    const onKey = (e) => { if (e.key === "Escape") onOpenChange(false); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, onOpenChange, source]);

  const hasVideo = !!DEMO_VIDEO_URL;

  const handleTime = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    trackDemoPercent(Math.round((v.currentTime / v.duration) * 100), source);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="watch-demo-overlay"
          className="fixed inset-0 z-[120] flex items-center justify-center p-4 sm:p-6"
          style={{ background: "rgba(15,23,42,0.7)", backdropFilter: "blur(6px)" }}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={() => onOpenChange(false)}
          role="dialog" aria-modal="true" aria-label="RecoverOS product demo"
        >
          <motion.div
            data-testid="watch-demo-modal"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.96, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
            className="relative w-full max-w-4xl bg-white overflow-hidden"
            style={{ borderRadius: 16, boxShadow: "0 30px 80px -20px rgba(11,61,145,0.5)" }}
          >
            <button
              onClick={() => onOpenChange(false)} aria-label="Close" data-testid="watch-demo-close"
              className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/80 text-slate-600 shadow hover:bg-white hover:text-slate-900"
            >
              <X size={18} strokeWidth={2.5} />
            </button>

            <div className="relative aspect-video bg-slate-900">
              {hasVideo ? (
                isYouTube(DEMO_VIDEO_URL) ? (
                  <iframe
                    className="h-full w-full" src={DEMO_VIDEO_URL + "?autoplay=1&rel=0"}
                    title="RecoverOS Demo" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen
                    data-testid="watch-demo-iframe"
                  />
                ) : (
                  <video
                    ref={videoRef} className="h-full w-full" src={DEMO_VIDEO_URL} poster={DEMO_POSTER}
                    controls autoPlay onTimeUpdate={handleTime} data-testid="watch-demo-video"
                  />
                )
              ) : (
                <div className="absolute inset-0">
                  <img src={DEMO_POSTER} alt="RecoverOS demo preview" className="h-full w-full object-cover opacity-70" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6" style={{ background: "rgba(11,61,145,0.55)" }}>
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 text-[#0B3D91]"><Play size={26} fill="currentColor" /></div>
                    <h3 className="mt-4 text-xl font-bold text-white">Full 4-minute walkthrough coming soon</h3>
                    <p className="mt-1 max-w-md text-sm text-white/85">Meanwhile, explore RecoverOS with the options below. Start free with Intro PAYG — ₹0 for 3 months, up to 250 email communications.</p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-5 py-4 border-t border-slate-200">
              <div className="text-sm text-slate-500">RecoverOS product demo · {DEMO_DURATION}</div>
              <div className="flex flex-wrap gap-2 justify-center">
                {onSample && (
                  <button
                    onClick={() => { logEvent("demo_cta_clicked", { cta: "sample", source }); onOpenChange(false); onSample(); }}
                    className="inline-flex items-center gap-1.5 bg-[#0B3D91] text-white rounded-none px-4 py-2.5 text-sm font-bold hover:bg-[#0a347d] transition-colors"
                    data-testid="watch-demo-sample"
                  ><FolderOpen size={15} /> Explore Sample</button>
                )}
                {onImport && (
                  <button
                    onClick={() => { logEvent("demo_cta_clicked", { cta: "import", source }); onOpenChange(false); onImport(); }}
                    className="inline-flex items-center gap-1.5 border-2 border-[#0B3D91] text-[#0B3D91] rounded-none px-4 py-2.5 text-sm font-bold hover:bg-[#0B3D91]/[0.05] transition-colors"
                    data-testid="watch-demo-import"
                  ><Upload size={15} /> Import Portfolio</button>
                )}
                {onStartTrial && (
                  <button
                    onClick={() => { logEvent("demo_cta_clicked", { cta: "trial", source }); onStartTrial(); }}
                    className="bg-[#16A34A] text-white rounded-none px-5 py-2.5 text-sm font-bold hover:bg-[#138a3e] transition-colors"
                    data-testid="watch-demo-trial"
                  >Start Free — Intro PAYG</button>
                )}
                {onBookDemo && (
                  <button
                    onClick={() => { logEvent("book_demo_clicked", { source }); onOpenChange(false); onBookDemo(); }}
                    className="border-2 border-[#0B3D91] text-[#0B3D91] rounded-none px-5 py-2.5 text-sm font-bold hover:bg-[#0B3D91]/[0.05] transition-colors"
                    data-testid="watch-demo-book"
                  >Book Live Demo</button>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
